/**
 * C9 · Streak & completion at a glance.
 * E2 · Per-exercise accuracy trends (small multiples).
 * E3 · Range development band + retake link.
 * E8 · "Your quarter in review" recap card (share gated by coach).
 */

import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { t } from '../../lib/i18n.ts';
import { Button, Card, Spinner } from '../../lib/ui.tsx';
import { RangeBand, Ring, Sparkline } from '../../components/charts.tsx';

interface StudentAnalytics {
  completionRate: number | null;
  streak: number;
  accuracyTrends: { exerciseId: string; exerciseName: string; points: { t: string; score: number }[] }[];
  rangeHistory: { date: string; lowMidi: number; highMidi: number; lowNote: string; highNote: string }[];
}

interface Recap {
  shareEnabled: boolean;
  practiceDays: number;
  totalMinutes: number;
  takesSubmitted: number;
  longestStreak: number;
  rangeGainedSemis: number;
  currentRange: { low: string; high: string } | null;
  avgScore: number | null;
}

export default function ProgressPage() {
  const { me } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['student-analytics', me?.id],
    queryFn: () => api<StudentAnalytics>(`/api/analytics/student/${me!.id}`),
    enabled: !!me,
  });
  const { data: recap } = useQuery({
    queryKey: ['recap', me?.id],
    queryFn: () => api<Recap>(`/api/analytics/recap/${me!.id}`),
    enabled: !!me,
  });

  if (isLoading || !data) return <Spinner />;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-bold">{t('progress.title', 'Progress')}</h1>

      {/* C9 · streak + completion */}
      <Card>
        <div className="flex items-center justify-around">
          <div className="text-center">
            <p className="text-3xl font-bold text-brand-700">🔥 {data.streak}</p>
            <p className="text-xs text-slate-500">{t('progress.streak', 'day streak')}</p>
          </div>
          <Ring value={data.completionRate ?? 0} label={t('progress.completion', '4-week completion')} />
        </div>
      </Card>

      {/* E2 · accuracy trends */}
      <Card title={t('progress.accuracy', 'Accuracy by exercise')}>
        {data.accuracyTrends.length === 0 ? (
          <p className="text-xs text-slate-400">
            {t('progress.noscores', 'Practice a few exercises to see your trends here.')}
          </p>
        ) : (
          <div className="space-y-4">
            {data.accuracyTrends.map((trend) => (
              <div key={trend.exerciseId}>
                <p className="mb-1 text-xs font-medium text-slate-600">{trend.exerciseName}</p>
                <Sparkline
                  points={trend.points.map((p) => ({ x: Date.parse(p.t), y: p.score }))}
                  yDomain={[0, 100]}
                  height={72}
                />
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* E3 · range development */}
      <Card
        title={t('progress.range', 'Vocal range')}
        actions={
          <Link to="/app/range-test" className="text-xs font-medium text-brand-700 hover:underline">
            {t('progress.retake', 'Retake range test')} →
          </Link>
        }
      >
        <RangeBand history={data.rangeHistory} />
      </Card>

      {/* E8 · quarterly recap */}
      {recap && <RecapCard recap={recap} name={me?.name ?? ''} />}
    </div>
  );
}

function RecapCard({ recap, name }: { recap: Recap; name: string }) {
  const [copied, setCopied] = useState(false);

  const summary = [
    `🎤 ${name}'s quarter with Solfo:`,
    `${recap.practiceDays} practice days · ${recap.totalMinutes} minutes sung`,
    `${recap.takesSubmitted} takes submitted · longest streak ${recap.longestStreak} days`,
    recap.currentRange
      ? `Range: ${recap.currentRange.low}–${recap.currentRange.high} (+${recap.rangeGainedSemis} semitones)`
      : null,
    recap.avgScore !== null ? `Average accuracy: ${recap.avgScore}` : null,
  ]
    .filter(Boolean)
    .join('\n');

  async function share() {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'My quarter in review', text: summary });
        return;
      } catch {
        /* cancelled — fall through to clipboard */
      }
    }
    await navigator.clipboard.writeText(summary);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const tiles: { label: string; value: string }[] = [
    { label: t('recap.days', 'practice days'), value: String(recap.practiceDays) },
    { label: t('recap.minutes', 'minutes sung'), value: String(recap.totalMinutes) },
    { label: t('recap.takes', 'takes submitted'), value: String(recap.takesSubmitted) },
    { label: t('recap.streak', 'longest streak'), value: `${recap.longestStreak}d` },
    { label: t('recap.rangeGain', 'semitones gained'), value: `+${recap.rangeGainedSemis}` },
    ...(recap.currentRange
      ? [{ label: t('recap.range', 'current range'), value: `${recap.currentRange.low}–${recap.currentRange.high}` }]
      : []),
    ...(recap.avgScore !== null ? [{ label: t('recap.avg', 'avg accuracy'), value: String(recap.avgScore) }] : []),
  ];

  return (
    <Card
      className="border-brand-200 bg-gradient-to-br from-brand-50 to-white"
      title={t('recap.title', 'Your quarter in review ✨')}
      actions={
        recap.shareEnabled ? (
          <Button variant="secondary" onClick={() => void share()}>
            {copied ? t('recap.copied', 'Copied!') : t('recap.share', 'Share')}
          </Button>
        ) : undefined
      }
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-lg bg-white/80 p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-slate-900">{tile.value}</p>
            <p className="mt-0.5 text-[11px] text-slate-500">{tile.label}</p>
          </div>
        ))}
      </div>
      {!recap.shareEnabled && (
        <p className="mt-3 text-[11px] text-slate-400">{t('recap.shareHint', 'Sharing is enabled by your coach')}</p>
      )}
    </Card>
  );
}
